/**
 * 500's RankingContext (rules §4, ARCHITECTURE.md §2.3).
 *
 * The whole point of the abstraction is that the two hard mechanics are data,
 * not branches in the trick-winner or in legality:
 *
 *  - **The left bower's effective suit is the trump suit.** Rules §4.1/§5.1:
 *    "for purposes of following suit, etc. the joker and left bower behave in
 *    all respects as members of the trump suit". Putting that in
 *    `effectiveSuit` gets both halves for free — it cannot follow its printed
 *    suit (edge #2) and it MUST follow trump (edge #3) — with no special case
 *    anywhere else.
 *  - **The floating Joker in NT/misère is simply the only trump in the pack**,
 *    which is exactly why "it wins the trick to which it is played" needs no
 *    code at all.
 */
import { faceOf, sameColourSuit, } from '../../core/cards.js';
import { isMisereMode } from './types.js';
/** Strength bands. Only compared within one effective suit, or between trumps,
 *  so the absolute numbers matter only for readability and for bots that want a
 *  single global "how good is this card" scale. */
const JOKER_STRENGTH = 200;
const RIGHT_BOWER_STRENGTH = 199;
const LEFT_BOWER_STRENGTH = 198;
const TRUMP_BASE = 100;
/** Top of a plain suit is the Ace at 14, so a nominated Joker sits at 15. */
const NOMINATED_JOKER_STRENGTH = 15;
/** Trickster's "Joker Low in Nullo" variant: below the lowest card in the deck. */
const LOW_JOKER_STRENGTH = 1;
export function isRightBower(card, trump) {
    const f = faceOf(card);
    return f.rank === 11 && f.suit === trump;
}
/** The jack of the OTHER suit of trump's colour. It is a trump for the whole
 *  hand, and its printed suit spends the hand with no jack at all (edge #20). */
export function isLeftBower(card, trump) {
    const f = faceOf(card);
    return f.rank === 11 && f.suit === sameColourSuit(trump);
}
/** Build the context for a hand. `contract` may be null before the auction
 *  finishes; the result is then a plain no-trump, floating-Joker context, which
 *  is a safe thing for a UI to sort a hand with. */
export function fhRanking(contract, extra, config) {
    const mode = contract?.mode ?? 'nt';
    const trump = contract?.trumpSuit ?? null;
    // ---- Suit contract: Joker, right bower and left bower all become trumps.
    if (mode === 'suit' && trump !== null) {
        const effectiveSuit = (card) => {
            if (card === 'JOKER')
                return trump; // rules §5.1: the Joker is just the top trump
            if (isLeftBower(card, trump))
                return trump; // rules §4.1
            return faceOf(card).suit;
        };
        const strength = (card) => {
            if (card === 'JOKER')
                return JOKER_STRENGTH;
            if (isRightBower(card, trump))
                return RIGHT_BOWER_STRENGTH;
            if (isLeftBower(card, trump))
                return LEFT_BOWER_STRENGTH;
            const f = faceOf(card);
            return f.suit === trump ? TRUMP_BASE + (f.rank ?? 0) : (f.rank ?? 0);
        };
        return {
            trumpSuit: trump,
            effectiveSuit,
            isTrump: (card) => effectiveSuit(card) === trump,
            strength,
        };
    }
    // ---- No trumps / Misère / Open Misère: no bowers exist at all (rules §4.3),
    // every jack ranks normally in its own printed suit. Only the Joker is odd.
    const misere = isMisereMode(mode);
    const jokerLow = misere && config.jokerLowInMisere;
    const nominated = extra.jokerNominated;
    if (nominated !== null) {
        // Mode 1 (rules §4.3): declared before the lead to trick 1, the Joker is
        // permanently the top card of that suit — it follows suit like any other
        // card of it, and it is NOT trump, so a different suit's lead beats it.
        return {
            trumpSuit: null,
            effectiveSuit: (card) => (card === 'JOKER' ? nominated : faceOf(card).suit),
            isTrump: () => false,
            strength: (card) => card === 'JOKER'
                ? (jokerLow ? LOW_JOKER_STRENGTH : NOMINATED_JOKER_STRENGTH)
                : (faceOf(card).rank ?? 0),
        };
    }
    // Mode 2 (rules §4.3): the Joker belongs to no suit and is the highest card in
    // the pack. Modelling it as "the only trump" is what makes currentWinner do
    // the right thing with zero 500-specific code.
    return {
        trumpSuit: null,
        effectiveSuit: (card) => (card === 'JOKER' ? null : faceOf(card).suit),
        isTrump: (card) => card === 'JOKER' && !jokerLow,
        strength: (card) => card === 'JOKER'
            ? (jokerLow ? LOW_JOKER_STRENGTH : JOKER_STRENGTH)
            : (faceOf(card).rank ?? 0),
    };
}
//# sourceMappingURL=ranking.js.map